import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import {
    canGrantGroup,
    canManageCrmGroups,
    canManageUsers,
    canModifyUser,
    canUseSupportMode,
    getRoleLabel,
    sortRoles,
} from '../utils.ts';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import { url } from '../../core/utils/link.ts';

import {
    DELETE_USER,
    GET_ROLES,
    GET_TEAMS,
    GET_USER,
    GET_USER_EMAIL_ADDRESSES,
    GET_USERS,
    INVITE_USER,
    PERSONAL_SMART_INBOX_INTEGRATIONS,
    RESET_PASSWORD,
    RESET_USER_MFA,
    UPDATE_USER,
} from '../queries.ts';
import { USERS_ADD_PATH, USERS_EDIT_PATH, USERS_PATH } from '../constants.ts';
import { DEFAULT_LANGUAGE, LANGUAGE_OPTIONS } from '../../../constants.ts';

import Layout from '../../core/components/Layout.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { faEllipsisV, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Page from '../../core/components/Page';
import FilterDropdown from '../../core/components/Filter.tsx';
import OrderByDropdown from '../../core/components/OrderByDropdown.tsx';
import Table, { RowControlIconButton } from '../../core/components/Table.tsx';
import { RowControlButtonProps } from '../../core/components/Table.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import { IUser } from '../interfaces.ts';
import Modal from '../../core/components/Modal.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import TagsField from '../../core/components/Fields/TagsField.tsx';
import { Controller, useForm } from 'react-hook-form';
import FormField from '../../core/components/FormField.tsx';
import Button from '../../core/components/Button.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { Outlet } from 'react-router';
import CheckboxGroup from '../../core/components/CheckboxGroup.tsx';
import useSearchParamState from '../../core/utils/useSearchParamState.tsx';
import usePersistedSearchParams from '../../core/utils/usePersistedSearchParams.ts';
import useUpdateEffect from '../../core/utils/useUpdateEffect.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import TablePagination from '../../core/components/TablePagination.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import { ConnectionStatus, getPersonalSmartInboxConnectionStatus } from '../../document/constants.ts';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import ManagePersonalSmartInboxModal from '../components/PersonalSmartInbox/ManagePersonalSmartInboxModal.tsx';
import RolePermissionMatrix from '../components/RolePermissionMatrix.tsx';

interface UserFormModalOutletContext {
    roles: any[];
    teams: any[];
    users: IUser[];
    totalCount: number;
    refetch: () => Promise<any>;
}

const PAGE_PARAM = 'page';
const PERSISTED_PARAMS = ['role', 'crmRole', 'team', 'personalSmartInbox', 'sort', 'pageSize'];
const MAX_ITEMS = 500;
const ITEMS_PER_PAGE = 10;

const MoreIcon = withIcon(faEllipsisV);
const AddIcon = withIcon(faPlus);
const DotIcon = withIcon(faCircle);

const FORM_DEFAULT_VALUES = {
    firstName: '',
    lastName: '',
    email: '',
    emailAliases: [],
    language: DEFAULT_LANGUAGE,
    roleIds: [],
    crmRoleIds: [],
    teamIds: [],
};

const UserFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    user,
    roles,
    crmRoles,
    teams,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (formData: any) => void;
    onClose: () => void;
    user?: IUser;
    roles: any[];
    crmRoles: any[];
    teams: any[];
}) => {
    const { t } = useTranslation('users');
    const { user: viewer } = useApplicationContext();

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (user) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                ...(user && {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    emailAliases: user.emailAliases || [],
                    language: user.language || DEFAULT_LANGUAGE,
                    roleIds: user.groups?.map((group) => group.id) || [],
                    crmRoleIds: canManageCrmGroups(viewer) ? user.crmGroups?.map((group) => group.id) || [] : [],
                    teamIds: user.teams?.map((team) => team.id) || [],
                }),
            });
        } else if (teams) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                teamIds: teams.filter((team) => team.isDefault).map((team) => team.id),
            });
        }
    }, [user, teams]);

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            className="md:min-w-[42rem]"
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(USERS_PATH, undefined, { keepSearch: true })}>{t('form.cancelButton')}</Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="firstName"
                    control={form.control}
                    rules={{ required: t('form.firstNameRequired') }}
                    render={({ field }) => (
                        <FormField label={t('form.firstName')} required={true} error={formErrors.firstName}>
                            <StringField required={true} {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="lastName"
                    control={form.control}
                    rules={{ required: t('form.lastNameRequired') }}
                    render={({ field }) => (
                        <FormField label={t('form.lastName')} required={true} error={formErrors.lastName}>
                            <StringField required={true} {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="email"
                    control={form.control}
                    rules={{ required: t('form.emailRequired') }}
                    render={({ field }) => (
                        <FormField label={t('form.email')} required={true} error={formErrors.email}>
                            <StringField required={true} {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="emailAliases"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('form.emailAliases')}
                            error={formErrors.emailAliases}
                            helpText={t('form.emailAliasesHelpText')}
                        >
                            <TagsField {...field} onValueChange={field.onChange} />
                        </FormField>
                    )}
                />

                <Controller
                    name="language"
                    control={form.control}
                    rules={{ required: t('form.languageRequired') }}
                    render={({ field }) => (
                        <FormField label={t('form.language')} required={true} error={formErrors.language}>
                            <SelectField {...field} options={LANGUAGE_OPTIONS} required />
                        </FormField>
                    )}
                />

                <Controller
                    name="roleIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('form.roles')}
                            error={formErrors.roleIds}
                            helpText={
                                roles?.some((role) => !canGrantGroup(viewer, role.name))
                                    ? `${t('form.rolesAdditiveHelpText')} ${t('form.rolesHelpText')}`
                                    : t('form.rolesAdditiveHelpText')
                            }
                        >
                            <RolePermissionMatrix
                                roles={
                                    roles?.map((role) => ({
                                        ...role,
                                        disabled: !canGrantGroup(viewer, role.name),
                                    })) || []
                                }
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                {canManageCrmGroups(viewer) && (
                    <Controller
                        name="crmRoleIds"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('form.crmRoles')} error={formErrors.crmRoleIds}>
                                <CheckboxGroup
                                    {...field}
                                    options={crmRoles?.map((role) => ({ value: role.id, label: role.name })) || []}
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                />
                            </FormField>
                        )}
                    />
                )}

                <Controller
                    name="teamIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('form.teams')} error={formErrors.teamIds}>
                            <CheckboxGroup
                                {...field}
                                options={
                                    teams?.map((team) => ({
                                        value: team.id,
                                        label: !team.isDefault ? (
                                            team.name
                                        ) : (
                                            <span className="flex gap-2 items-center">
                                                <span>{viewer?.customer?.name || t('overview.defaultTeam')}</span>
                                                <span className="text-xs text-content-brand-strong font-medium bg-surface-brand-medium-default px-1.5 py-0.5 rounded">
                                                    {t('overview.defaultTeamBadge')}
                                                </span>
                                            </span>
                                        ),
                                        disabled: team.isDefault,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

export const AddUserFormModal = () => {
    const { t } = useTranslation('users');
    const { teams, roles, crmRoles, refetch } = useOutletContext<UserFormModalOutletContext>();
    const { user: viewer } = useApplicationContext();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [inviteUser] = useMutation(INVITE_USER);
    const handleCreate = async (formData) => {
        try {
            await inviteUser({
                variables: {
                    email: formData.email,
                    emailAliases: formData.emailAliases,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    groups: formData.roleIds,
                    crmGroups: canManageCrmGroups(viewer) ? formData.crmRoleIds : undefined,
                    teams: formData.teamIds,
                    language: formData.language,
                },
            });
            await refetch();
            publishToast({ description: t('form.createSuccess'), status: 'success' });
            navigate(url(USERS_PATH, undefined, { keepSearch: true }));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('form.createError'), status: 'error' });
        }
    };

    return (
        <UserFormModal
            title={t('form.createTitle')}
            submitButtonLabel={t('form.createButton')}
            onSubmit={handleCreate}
            onClose={() => navigate(url(USERS_PATH, undefined, { keepSearch: true }))}
            teams={teams}
            roles={roles}
            crmRoles={crmRoles}
        />
    );
};

export const EditUserFormModal = () => {
    const { t } = useTranslation('users');
    const { teams, roles, crmRoles, refetch } = useOutletContext<UserFormModalOutletContext>();

    const { user: viewer } = useApplicationContext();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const { userId } = useParams();
    const { data: editUserData } = useQuery(GET_USER, { skip: !userId, variables: { id: userId } });
    const user = editUserData?.user;

    const [updateUser] = useMutation(UPDATE_USER);
    const handleUpdate = async (formData) => {
        try {
            await updateUser({
                variables: {
                    id: user.id,
                    email: formData.email,
                    emailAliases: formData.emailAliases,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    groups: formData.roleIds,
                    crmGroups: canManageCrmGroups(viewer) ? formData.crmRoleIds : undefined,
                    teams: formData.teamIds,
                    language: formData.language,
                },
            });
            await refetch();
            publishToast({ description: t('form.updateSuccess'), status: 'success' });
            navigate(url(USERS_PATH, undefined, { keepSearch: true }));
        } catch (error) {
            publishToast({ description: t('form.updateError'), status: 'error' });
            console.error(error);
        }
    };

    return (
        <UserFormModal
            title={t('form.updateTitle')}
            submitButtonLabel={t('form.updateButton')}
            onSubmit={handleUpdate}
            onClose={() => navigate(url(USERS_PATH, undefined, { keepSearch: true }))}
            user={user}
            teams={teams}
            roles={roles}
            crmRoles={crmRoles}
        />
    );
};

const UserMoreButton = ({
    user,
    roles,
    refetch,
    integration,
    onRefresh,
    ...props
}: RowControlButtonProps & {
    user: IUser;
    roles: any[];
    refetch: () => Promise<any>;
    integration: any | null;
    onRefresh: () => Promise<any>;
}) => {
    const { t } = useTranslation('users');
    const { publishToast } = useToaster();

    const { user: viewer } = useApplicationContext();
    const isCurrentUser = viewer.id === user.id;
    const isModifiable = canModifyUser(
        viewer,
        user,
        roles.map((role) => role.name)
    );
    const isAdminConsentGranted = viewer?.customer?.personalInboxAdminConsent?.isGranted ?? false;
    const isAdminConsentUpToDate = viewer?.customer?.personalInboxAdminConsent?.isUpToDate ?? false;

    const [deleteUser] = useMutation(DELETE_USER);
    const [resetPassword] = useMutation(RESET_PASSWORD);
    const [resetUserMfa, { loading: isResettingMfa }] = useMutation(RESET_USER_MFA);

    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isResetModalVisible, setResetModalVisible] = useState(false);
    const [isResetMfaModalVisible, setResetMfaModalVisible] = useState(false);
    const [isManagePersonalSmartInboxModalVisible, setIsManagePersonalSmartInboxModalVisible] = useState(false);

    const handleDeleteUser = async () => {
        try {
            const { data } = await deleteUser({ variables: { id: user.id } });
            if (!data?.deleteUser?.success) {
                throw new Error('deleteUser did not report success');
            }
            publishToast({ description: t('overview.deleteSuccess', { user }) });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('overview.deleteError', { user }) });
        } finally {
            await refetch();
            setDeleteModalVisible(false);
        }
    };

    const handleResetPassword = async () => {
        try {
            await resetPassword({ variables: { email: user.email } });
            publishToast({
                description: t('overview.resetPasswordSuccess', { user, email: user.email }),
                status: 'success',
            });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('overview.resetPasswordError', { user }), status: 'error' });
        } finally {
            setResetModalVisible(false);
        }
    };

    const handleResetMfa = async () => {
        if (isResettingMfa) return;
        try {
            const { data } = await resetUserMfa({ variables: { userId: user.id } });
            if (!data?.resetUserMfa?.success) {
                throw new Error('resetUserMfa did not report success');
            }
            publishToast({ description: t('resetMfa.success', { email: user.email }), status: 'success' });
            setResetMfaModalVisible(false);
        } catch {
            publishToast({ description: t('resetMfa.error'), status: 'error' });
        }
    };

    if (!isModifiable) {
        return (
            <span className="cursor-not-allowed">
                <RowControlIconButton {...props} disabled>
                    <MoreIcon />
                </RowControlIconButton>
            </span>
        );
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <RowControlIconButton {...props}>
                        <MoreIcon />
                    </RowControlIconButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content align="end">
                    <DropdownMenu.Item asChild>
                        <Link to={url(USERS_EDIT_PATH, { userId: user.id }, { keepSearch: true })}>
                            {t('overview.moreMenu.edit')}
                        </Link>
                    </DropdownMenu.Item>

                    <DropdownMenu.Item onClick={() => setResetModalVisible(true)}>
                        {t('overview.moreMenu.resetPassword')}
                    </DropdownMenu.Item>

                    {canUseSupportMode(viewer) && (
                        <DropdownMenu.Item onClick={() => setResetMfaModalVisible(true)} disabled={!user.hasMfaDevice}>
                            {t('resetMfa.title')}
                        </DropdownMenu.Item>
                    )}

                    {integration && (
                        <DropdownMenu.Item onClick={() => setIsManagePersonalSmartInboxModalVisible(true)}>
                            {t('overview.moreMenu.manageSmartInbox')}
                        </DropdownMenu.Item>
                    )}

                    <DropdownMenu.Separator />

                    <DropdownMenu.Item danger onClick={() => setDeleteModalVisible(true)} disabled={isCurrentUser}>
                        {t('overview.moreMenu.delete')}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu>

            {isDeleteModalVisible && (
                <ConfirmModal
                    onConfirm={handleDeleteUser}
                    onCancel={() => setDeleteModalVisible(false)}
                    title={t('overview.confirmDeleteTitle')}
                    labelConfirm={t('overview.confirmDeleteButton')}
                    danger
                >
                    {t('overview.confirmDelete', { user })}
                </ConfirmModal>
            )}
            {isResetModalVisible && (
                <ConfirmModal
                    onConfirm={handleResetPassword}
                    onCancel={() => setResetModalVisible(false)}
                    title={t('overview.confirmResetPasswordTitle')}
                    labelConfirm={t('overview.confirmResetPasswordButton')}
                >
                    {t('overview.confirmResetPassword', { user })}
                </ConfirmModal>
            )}
            {isResetMfaModalVisible && (
                <ConfirmModal
                    title={t('resetMfa.title')}
                    labelConfirm={t(isResettingMfa ? 'resetMfa.submitting' : 'resetMfa.confirm')}
                    confirmDisabled={isResettingMfa}
                    onConfirm={handleResetMfa}
                    onCancel={() => setResetMfaModalVisible(false)}
                    danger
                >
                    {t('resetMfa.description', { user })}
                </ConfirmModal>
            )}
            {isManagePersonalSmartInboxModalVisible && integration && (
                <ManagePersonalSmartInboxModal
                    integration={integration}
                    isAdminConsentGranted={isAdminConsentGranted}
                    isAdminConsentUpToDate={isAdminConsentUpToDate}
                    onClose={() => {
                        setIsManagePersonalSmartInboxModalVisible(false);
                        onRefresh();
                    }}
                    onRefresh={onRefresh}
                />
            )}
        </>
    );
};

const UserRow = ({ user, moreButton, personalSmartInboxes }) => {
    const { t } = useTranslation('users');

    const { user: viewer } = useApplicationContext();
    const isCurrentUser = viewer.id === user.id;

    const getPersonalSmartInboxStatus = (userId) => {
        return (personalSmartInboxes as [])?.filter((inbox) => inbox['createdBy']['id'] === userId);
    };
    const renderPersonalSmartInboxStatus = (status: ConnectionStatus) => {
        return (
            <span
                className={classnames(
                    'text-content-strong font-medium text-sm items-center border-[1px]  px-2 py-0.5 rounded',
                    status === ConnectionStatus.Connected &&
                        'text-content-brand-medium border-line-success-weak bg-surface-success-weak ',
                    status === ConnectionStatus.Disconnected &&
                        'text-content-default border-surface-strong-default bg-surface-medium',
                    status === ConnectionStatus.Paused &&
                        'text-content-warning-default border-line-warning-weak bg-surface-warning-weak',
                    status === ConnectionStatus.Error &&
                        'text-content-danger-strong border-line-danger-weak bg-surface-danger-weak'
                )}
            >
                {status !== ConnectionStatus.Disconnected && (
                    <DotIcon
                        className={classnames(
                            'text-[10px] pr-2 pb-[1px]',
                            status === ConnectionStatus.Connected && 'text-content-success-weak',
                            status === ConnectionStatus.Paused && 'text-content-warning-weak',
                            status === ConnectionStatus.Error && 'text-content-danger-weak'
                        )}
                    />
                )}
                {t(`overview.table.status.${status.toLowerCase()}`)}
            </span>
        );
    };

    const status = getPersonalSmartInboxConnectionStatus(getPersonalSmartInboxStatus(user.id)?.[0]);

    return (
        <>
            <Table.Row>
                <Table.Cell>
                    <div className="flex gap-3 justify-start items-center">
                        <div className="flex flex-col">
                            <div>
                                {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-content-medium">{user.email}</div>
                        </div>

                        {isCurrentUser && (
                            <span className="text-xs text-content-brand-strong font-medium bg-surface-brand-medium-default px-1.5 py-0.5 rounded">
                                {t('overview.youBadge')}
                            </span>
                        )}
                    </div>
                </Table.Cell>
                <Table.Cell>
                    {sortRoles(user.groups)
                        .map((group) => getRoleLabel(t, group.name))
                        .join(', ')}
                </Table.Cell>
                {canManageCrmGroups(viewer) && (
                    <Table.Cell>{user.crmGroups.map((group) => group.name).join(', ')}</Table.Cell>
                )}
                <Table.Cell>
                    {user.teams
                        .map((team) =>
                            !team.isDefault ? team.name : viewer?.customer?.name || t('overview.defaultTeam')
                        )
                        .join(', ')}
                </Table.Cell>
                <Table.Cell>{renderPersonalSmartInboxStatus(status)}</Table.Cell>
                <Table.Cell sticky="right">{moreButton}</Table.Cell>
            </Table.Row>
        </>
    );
};

const UsersPage = ({ user: viewer }: any) => {
    const { t } = useTranslation('users');

    const [activePage, setActivePage] = useSearchParamState(PAGE_PARAM, '1');
    const [roleFilter, setRoleFilter] = useSearchParamState('role', '');
    const [crmRoleFilter, setCrmRoleFilter] = useSearchParamState('crmRole', '');
    const [teamFilter, setTeamFilter] = useSearchParamState('team', '');
    const [personalSmartInboxFilter, setPersonalSmartInboxFilter] = useSearchParamState('personalSmartInbox', '');

    const [sort, setSort] = useSearchParamState('sort', 'lastName');
    const [itemsPerPage, setItemsPerPage] = useSearchParamState('pageSize', ITEMS_PER_PAGE.toString());
    const isRestoringSearchParams = usePersistedSearchParams({
        storageKey: `users-${viewer?.customer?.id}`,
        keys: PERSISTED_PARAMS,
    });

    const [searchParams, setSearchParams] = useSearchParams();
    const handleClearAllClick = () => setSearchParams(() => ({}));
    const showClearAll = searchParams.has('role') || searchParams.has('team');

    useUpdateEffect(() => {
        // passed in search params might be outdated but window.location.search is always up-to-date
        const params = new URLSearchParams(window.location.search);
        if (!params.has(PAGE_PARAM)) return;
        params.delete(PAGE_PARAM);
        setSearchParams(params);
    }, [roleFilter, teamFilter, personalSmartInboxFilter, sort, itemsPerPage]);

    const parsedActivePage = parseInt(activePage);
    const parsedItemsPerPage = parseInt(itemsPerPage);

    let [sortField, sortOrder] = sort.startsWith('-') ? [sort.slice(1), 'desc'] : [sort, 'asc'];

    const SMART_INBOX_STATUS_MAP = {
        [ConnectionStatus.Connected]: 'ENABLED',
        [ConnectionStatus.Paused]: 'DISABLED',
        [ConnectionStatus.Disconnected]: 'NOT_CONNECTED',
        [ConnectionStatus.Error]: 'ERROR',
    };

    const {
        data: currentData,
        previousData,
        loading,
        refetch,
    } = useQuery(GET_USERS, {
        skip: isRestoringSearchParams,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {
            from: (parsedActivePage - 1) * parsedItemsPerPage,
            size: parsedItemsPerPage,
            groupId: roleFilter || undefined,
            crmGroupId: crmRoleFilter || undefined,
            teamId: teamFilter || undefined,
            hasPersonalSmartInboxConnection: personalSmartInboxFilter
                ? SMART_INBOX_STATUS_MAP[personalSmartInboxFilter as ConnectionStatus]
                : undefined,
            sortField,
            sortOrder,
        },
    });
    const data = loading ? previousData : currentData;

    const client = useApolloClient();
    const getAllUserEmailAddresses = () =>
        client.query({
            query: GET_USER_EMAIL_ADDRESSES,
            variables: {
                from: 0,
                size: 1000,
                groupId: roleFilter || undefined,
                crmGroupId: crmRoleFilter || undefined,
                teamId: teamFilter || undefined,
            },
        });

    const { publishToast } = useToaster();
    const handleCopyEmailAddresses = async () => {
        const data = await getAllUserEmailAddresses();
        const text = data.data?.users?.users?.map((user) => user.email).join('\n');
        navigator.clipboard
            .writeText(text)
            .then(() =>
                publishToast({
                    description: t('overview.copyEmailAddressesSuccess', { count: totalCount }),
                    status: 'success',
                })
            )
            .catch(() => publishToast({ description: t('overview.copyEmailAddressesError'), status: 'error' }));
    };

    const { data: rolesData } = useQuery(GET_ROLES, { notifyOnNetworkStatusChange: true });
    const { data: teamsData } = useQuery(GET_TEAMS, { notifyOnNetworkStatusChange: true });
    const { data: personalSmartInboxesData, refetch: refetchPersonalSmartInboxes } = useQuery(
        PERSONAL_SMART_INBOX_INTEGRATIONS,
        {
            notifyOnNetworkStatusChange: true,
            errorPolicy: 'all',
        }
    );
    const roles = rolesData?.groups || [];
    const crmRoles = rolesData?.crmGroups || [];
    const teams = teamsData?.teams || [];
    const users = data?.users?.users || [];
    const personalSmartInboxes = personalSmartInboxesData?.personalSmartInboxIntegrations as [];
    const totalCount = data?.users?.totalCount || 0;
    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('overview.title')}</Page.HeaderTitle>

                    <div className="flex gap-2">
                        <Page.HeaderButton onClick={handleCopyEmailAddresses}>
                            {t('overview.copyEmailAddresses', { count: totalCount })}
                        </Page.HeaderButton>

                        {canManageUsers(viewer) && (
                            <Page.HeaderButton asChild>
                                <Link to={url(USERS_ADD_PATH, undefined, { keepSearch: true })}>
                                    <AddIcon />
                                    {t('overview.inviteEmployee')}
                                </Link>
                            </Page.HeaderButton>
                        )}
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <div className="flex flex-wrap gap-2 justify-start">
                        <FilterDropdown
                            allLabel={t('overview.filters.role.all')}
                            label={t('overview.filters.role.some')}
                            options={sortRoles<{ id: string; name: string }>(rolesData?.groups || []).map((role) => ({
                                value: role.id,
                                label: getRoleLabel(t, role.name),
                            }))}
                            selected={roleFilter ? [roleFilter] : []}
                            onSelectedChange={(values) => {
                                setRoleFilter(values[0] || '');
                            }}
                        />

                        {canManageCrmGroups(viewer) && (
                            <FilterDropdown
                                allLabel={t('overview.filters.crmRole.all')}
                                label={t('overview.filters.crmRole.some')}
                                options={
                                    crmRoles?.map((role) => ({
                                        value: role.id,
                                        label: role.name,
                                    })) || []
                                }
                                selected={crmRoleFilter ? [crmRoleFilter] : []}
                                onSelectedChange={(values) => {
                                    setCrmRoleFilter(values[0] || '');
                                }}
                            />
                        )}

                        <FilterDropdown
                            allLabel={t('overview.filters.team.all')}
                            label={t('overview.filters.team.some')}
                            options={
                                teamsData?.teams?.map((team) => ({
                                    value: team.id,
                                    label: !team.isDefault
                                        ? team.name
                                        : viewer?.customer?.name || t('overview.defaultTeam'),
                                })) || []
                            }
                            selected={teamFilter ? [teamFilter] : []}
                            onSelectedChange={(values) => setTeamFilter(values[0] || '')}
                        />
                        <FilterDropdown
                            allLabel={t('overview.table.personalSmartInbox')}
                            label={t('overview.table.personalSmartInbox')}
                            options={
                                Object.entries(ConnectionStatus).map(([key, value]) => ({
                                    value: value,
                                    label: t(`overview.table.status.${value.toLowerCase()}`),
                                })) || []
                            }
                            selected={personalSmartInboxFilter ? [personalSmartInboxFilter] : []}
                            onSelectedChange={(values) => {
                                setPersonalSmartInboxFilter(values[0] || '');
                            }}
                            renderOption={(option) => (
                                <span
                                    className={classnames(
                                        'text-content-strong font-medium text-sm flex items-center',
                                        personalSmartInboxFilter === ConnectionStatus.Connected &&
                                            'text-content-brand-medium'
                                    )}
                                >
                                    <DotIcon
                                        className={classnames(
                                            'text-[10px] pr-2 pb-[1px]',
                                            option.value === ConnectionStatus.Connected && 'text-content-success-weak',
                                            option.value === ConnectionStatus.Paused && 'text-content-warning-weak',
                                            option.value === ConnectionStatus.Disconnected &&
                                                'text-surface-strong-active',
                                            option.value === ConnectionStatus.Error && 'text-content-danger-weak'
                                        )}
                                    />
                                    {option.label}
                                </span>
                            )}
                        />

                        {showClearAll && (
                            <Button variant="ghost" onClick={handleClearAllClick}>
                                {t('overview.filters.clearAll')}
                            </Button>
                        )}

                        <OrderByDropdown
                            options={[
                                { value: 'firstName', label: t('overview.filters.sort.firstName') },
                                { value: 'lastName', label: t('overview.filters.sort.lastName') },
                            ]}
                            selected={sort}
                            onSelectedChange={setSort}
                            className="md:ml-auto"
                        />
                    </div>

                    <div>
                        <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                            <Table.Head>
                                <Table.Row>
                                    <Table.HeadCell>{t('overview.table.name')}</Table.HeadCell>
                                    <Table.HeadCell>{t('overview.table.roles')}</Table.HeadCell>
                                    {canManageCrmGroups(viewer) && (
                                        <Table.HeadCell>{t('overview.table.crmRoles')}</Table.HeadCell>
                                    )}
                                    <Table.HeadCell>{t('overview.table.teams')}</Table.HeadCell>
                                    <Table.HeadCell>{t('overview.table.personalSmartInbox')}</Table.HeadCell>
                                    <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                                </Table.Row>
                            </Table.Head>
                            <Table.Body>
                                {users.map((user) => {
                                    const userIntegration =
                                        (personalSmartInboxes as any[])?.find(
                                            (inbox) => inbox.createdBy?.id === user.id
                                        ) ?? null;
                                    return (
                                        <UserRow
                                            personalSmartInboxes={personalSmartInboxes}
                                            key={user.id}
                                            user={user}
                                            moreButton={
                                                <UserMoreButton
                                                    user={user}
                                                    roles={roles}
                                                    refetch={refetch}
                                                    integration={userIntegration}
                                                    onRefresh={refetchPersonalSmartInboxes}
                                                />
                                            }
                                        />
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </div>

                    {totalCount > 0 && (
                        <TablePagination
                            totalCount={totalCount}
                            activePage={parsedActivePage}
                            itemsPerPage={parsedItemsPerPage}
                            setActivePage={(page: number) => setActivePage(page ? page.toString() : '')}
                            setItemsPerPage={(itemsPerPage: number) =>
                                setItemsPerPage(itemsPerPage ? itemsPerPage.toString() : '')
                            }
                        />
                    )}
                </Page.Content>
            </Page>

            <Outlet context={{ roles, crmRoles, teams, users, totalCount, refetch }} />
        </Layout>
    );
};

export default UsersPage;
